/**
 * BarcaChatConversation - Main conversation component with Barcelona theme
 * Enhanced glass morphism with authentic FC Barcelona colors
 * Incorporates garnet red, Barcelona blue, and gold accents
 */

import { useState, useEffect, useRef } from "react";
import { AudioIndicator } from "./AudioIndicator";
import { MessageCircle, X, Mic, PhoneOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { useConversation } from "@elevenlabs/react";
import { useConversationHistory } from "../hooks/useConversationHistory";
import BackgroundStripes from "./BackgroundStripes";
import { convertUrlsToLinks } from "@/lib/urlUtils";
import { ListeningPlaceholder } from "./ListeningPlaceholder";
import { useBarcaChatConfig } from "../providers/BarcaChatProvider";
import { ChatMode } from "../types";

interface BarcaChatConversationProps {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    chatMode: ChatMode;
    setChatMode: (mode: ChatMode) => void;
    zIndex: number;
}

export const BarcaChatConversation = ({ isOpen, setIsOpen, chatMode, setChatMode, zIndex }: BarcaChatConversationProps) => {
    const { config } = useBarcaChatConfig();
    const [error, setError] = useState<string | null>(null);
    const [hasStarted, setHasStarted] = useState(false);
    const [micPermissionGranted, setMicPermissionGranted] = useState(false);

    const lastStateRef = useRef<'listening' | 'responding' | null>(null);
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const { messages, addMessage, clearHistory, isListening, setListeningState } = useConversationHistory();

    const scrollToBottom = (smooth: boolean = true) => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({
                behavior: smooth ? 'smooth' : 'auto',
                block: 'end'
            });
        }
    };

    // Dynamic scaling based on viewport height
    useEffect(() => {
      const updateScale = () => {
          const designHeight = 550;
          const bottomSpacing = 112;
          const availableHeight = window.innerHeight - bottomSpacing;
          const scale = Math.max(0.45, Math.min(1, availableHeight / designHeight));
          
          const autoscaleEl = document.querySelector('.barca-autoscale');
          if (autoscaleEl instanceof HTMLElement) {
              autoscaleEl.style.setProperty('--barca-scale', scale.toString());
          }
      };

      updateScale();
      window.addEventListener('resize', updateScale);
      return () => window.removeEventListener('resize', updateScale);
    }, []);

    useEffect(() => {
        if (messages.length > 0) {
            const timer = setTimeout(() => scrollToBottom(), 100);
            return () => clearTimeout(timer);
        }
    }, [messages]);

    const conversation = useConversation({
        onConnect: () => {
            // Connected to ElevenLabs AI
            setHasStarted(true);
            setError(null);
            config.onConversationStart?.();
        },
        onDisconnect: () => {
            // Disconnected from ElevenLabs AI
            config.onConversationEnd?.();
        },
        onMessage: (message) => {
            // Message received
            if (message.source === 'user') {
                setListeningState(false); // Hide listening placeholder when user message arrives
                addMessage(message.message, 'user');
            } else if (message.source === 'ai') {
                addMessage(message.message, 'assistant');
            }
        },
        onError: (error: any) => {
            // Conversation error
            const errorMessage = typeof error === 'string' ? error : error?.message || 'An error occurred';
            setError(errorMessage);
            config.onError?.(new Error(errorMessage));
        },
    });

    const [debouncedIsListening, setDebouncedIsListening] = useState(false);
    const [debouncedIsResponding, setDebouncedIsResponding] = useState(false);

    useEffect(() => {
        const isListening = conversation.status === 'connected' && !conversation.isSpeaking;
        const isResponding = conversation.status === 'connected' && conversation.isSpeaking;

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        if (isListening && lastStateRef.current !== 'listening') {
            lastStateRef.current = 'listening';
            debounceTimerRef.current = setTimeout(() => {
                setDebouncedIsListening(true);
                setDebouncedIsResponding(false);
                setListeningState(true);
            }, 300);
        } else if (isResponding && lastStateRef.current !== 'responding') {
            lastStateRef.current = 'responding';
            debounceTimerRef.current = setTimeout(() => {
                setDebouncedIsListening(false);
                setDebouncedIsResponding(true);
                setListeningState(false);
            }, 300);
        } else if (!isListening && !isResponding) {
            lastStateRef.current = null;
            debounceTimerRef.current = setTimeout(() => {
                setDebouncedIsListening(false);
                setDebouncedIsResponding(false);
                setListeningState(false);
            }, 300);
        }

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [conversation.status, conversation.isSpeaking, setListeningState]);

    const requestMicrophonePermission = async (): Promise<boolean> => {
        try {
            await navigator.mediaDevices.getUserMedia({ audio: true });
            setMicPermissionGranted(true);
            return true;
        } catch (err) {
            // Microphone permission error
            setError('Microphone access is required for voice conversations');
            return false;
        }
    };

    const startConversation = async () => {
        try {
            setError(null);

            if (!conversation.status || conversation.status === 'disconnected') {
                if (hasStarted) {
                    clearHistory();
                    setHasStarted(false);
                }
            }

            if (!micPermissionGranted) {
                const granted = await requestMicrophonePermission();
                if (!granted) return;
            }

            const conversationId = await conversation.startSession({
                agentId: config.agentId,
            } as any);

            // Conversation started successfully
            setIsOpen(true);
        } catch (err: any) {
            // Failed to start conversation
            const errorMessage = err.message || 'Failed to connect to AI assistant';
            setError(errorMessage);
            config.onError?.(new Error(errorMessage));
        }
    };

    const endConversation = async () => {
        try {
            await conversation.endSession();
        } catch (err) {
            // Error ending conversation
        }
    };

    const handleTalkToAgent = () => {
        if (conversation.status === 'connected') {
            endConversation();
        } else {
            startConversation();
        }
    };

    const selectChatMode = async (mode: 'voice' | 'text') => {
        if (mode === 'text') {
            // If using widget manager, request to open text widget
            if (config.useWidgetManager) {
                const { widgetManager } = await import('../lib/WidgetManager');
                await widgetManager.requestOpen('text');
            }

            // Call the text chat callback
            config.onTextChatSelected?.();

            // Hide this widget if configured to do so
            if (config.hideOnTextSelection !== false) {
                setIsOpen(false);
            }
        } else {
            setChatMode(mode);
        }
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (conversation.status === 'connected') {
                conversation.endSession();
            }
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    return (
        // NEW: autoscale wrapper (keeps the panel anchored & scaled on short screens)
        <div className="barca-autoscale">
          <div
            className="
              barca-chat-conversation
              bw-fixed bw-bottom-0 bw-right-0 bw-left-0 bw-top-0
              sm:bw-bottom-24 sm:bw-right-6 sm:bw-left-auto sm:bw-top-auto
              sm:bw-w-[28rem] sm:bw-rounded-3xl
              bw-overflow-hidden
              animate-scale-in
            "
            style={{ zIndex }}
          >
            {/* Barcelona glass morphism container */}
            <div className="bw-relative bw-h-full sm:bw-rounded-3xl bw-overflow-hidden bw-shadow-2xl">
              {/* Clean white background with subtle Barcelona accents */}
              <div className="bw-absolute bw-inset-0 bw-bg-gradient-to-br bw-from-white/95 bw-via-white/90 bw-to-white/95 bw-backdrop-blur-3xl" />
      
              {/* Background stripes */}
              <BackgroundStripes />
      
              {/* Barcelona colors accent border */}
              <div
                className="bw-absolute bw-inset-0 sm:bw-rounded-3xl bw-pointer-events-none"
                style={{
                  border: '2px solid transparent',
                  background:
                    'linear-gradient(to right, rgba(248, 113, 113, 0.2), rgba(59, 130, 246, 0.15), rgba(37, 99, 235, 0.2)) border-box',
                  WebkitMask:
                    'linear-gradient(white 0 0) padding-box, linear-gradient(white 0 0)',
                  WebkitMaskComposite: 'xor',
                  mask:
                    'linear-gradient(white 0 0) padding-box, linear-gradient(white 0 0)',
                  maskComposite: 'exclude',
                }}
              />
      
              <div className="bw-relative bw-h-full bw-flex bw-flex-col">
                {/* Header */}
                <div className="barca-chat-header bw-p-5 bw-border-b bw-border-gray-200/30 bw-bg-gradient-to-r bw-from-white/50 bw-via-white/60 bw-to-white/50 bw-backdrop-blur-sm">
                  <div className="bw-flex bw-items-center bw-justify-between">
                    <div className="bw-flex bw-items-center bw-gap-4">
                      <div className="bw-relative">
                        <div
                          className="bw-w-11 bw-h-11 bw-rounded-2xl bw-flex bw-items-center bw-justify-center bw-shadow-lg"
                          style={{
                            background:
                              'linear-gradient(to bottom right, #C0185A, #004DB8)',
                            boxShadow:
                              '0 10px 15px -3px rgba(192, 24, 90, 0.2), 0 4px 6px -2px rgba(192, 24, 90, 0.05)',
                          }}
                        >
                          <MessageCircle className="bw-w-5 bw-h-5 bw-text-white" />
                        </div>
                        <div
                          className="bw-absolute bw--inset-1 bw-rounded-2xl bw-blur bw-opacity-30 bw-animate-pulse"
                          style={{
                            background:
                              'linear-gradient(to bottom right, #C0185A, #004DB8)',
                          }}
                        />
                      </div>
                      <div>
                        <h3 className="bw-font-bold bw-text-slate-800 bw-text-lg">
                          {chatMode === 'selection'
                            ? 'Communication Options'
                            : chatMode === 'voice'
                            ? 'Ryan - AI Assistant'
                            : 'Text Interface'}
                        </h3>
                        {chatMode === 'voice' && (
                          <p
                            className={cn(
                              'bw-text-sm bw-font-medium bw-transition-all bw-duration-300',
                              conversation.status === 'connected'
                                ? ''
                                : 'bw-text-slate-500'
                            )}
                            style={{
                              color:
                                conversation.status === 'connected'
                                  ? debouncedIsListening
                                    ? '#004D98'
                                    : debouncedIsResponding
                                    ? '#A91B60'
                                    : '#A91B60'
                                  : undefined,
                            }}
                          >
                            {conversation.status === 'connected'
                              ? debouncedIsListening
                                ? 'Listening...'
                                : debouncedIsResponding
                                ? 'Speaking...'
                                : 'Active'
                              : 'Disconnected'}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="bw-flex bw-items-center bw-gap-2">
                      {chatMode === 'voice' &&
                        conversation.status === 'connected' && (
                          <button
                            onClick={endConversation}
                            className="barca-chat-end-btn bw-text-red-600 hover:bw-text-red-800 hover:bw-bg-red-100/50 bw-px-3 bw-py-2 bw-rounded-lg bw-transition-colors"
                          >
                            <PhoneOff className="bw-w-4 bw-h-4" />
                          </button>
                        )}
      
                      <button
                        onClick={() => {
                          setIsOpen(false);
                          config.onWidgetClose?.();
                        }}
                        className="barca-chat-close-btn bw-w-9 bw-h-9 bw-rounded-full bw-bg-white/40 hover:bw-bg-red-100/60 bw-flex bw-items-center bw-justify-center bw-transition-all bw-duration-200 hover:bw-scale-110 bw-backdrop-blur-sm bw-border bw-border-red-200/40"
                      >
                        <div
                          className="bw-absolute bw-inset-0 bw-rounded-full bw-pointer-events-none"
                          style={{
                            border: '1px solid transparent',
                            background:
                              'linear-gradient(to right, rgba(248, 113, 113, 0.2), rgba(59, 130, 246, 0.15), rgba(37, 99, 235, 0.2)) border-box',
                            WebkitMask:
                              'linear-gradient(white 0 0) padding-box, linear-gradient(white 0 0)',
                            WebkitMaskComposite: 'xor',
                            mask:
                              'linear-gradient(white 0 0) padding-box, linear-gradient(white 0 0)',
                            maskComposite: 'exclude',
                          }}
                        />
                        <X className="bw-w-4 bw-h-4 bw-text-slate-700" />
                      </button>
                    </div>
                  </div>
                </div>
      
                {/* Error */}
                {error && (
                  <div className="barca-chat-error bw-mx-4 bw-mt-4 bw-p-4 bw-bg-red-100/90 bw-border bw-border-red-300/60 bw-rounded-2xl bw-backdrop-blur-sm bw-shadow-lg bw-shadow-red-200/20">
                    <div className="bw-flex bw-items-start bw-justify-between">
                      <p className="bw-text-sm bw-text-red-700 bw-font-medium">
                        {error}
                      </p>
                      <button
                        onClick={() => setError(null)}
                        className="bw-ml-2 bw-text-red-500 hover:bw-text-red-700 bw-transition-colors"
                      >
                        <X className="bw-w-3 bw-h-3" />
                      </button>
                    </div>
                  </div>
                )}
      
                <div className="bw-flex bw-flex-col bw-h-full bw-overflow-hidden">
                  {/* Selection */}
                  {chatMode === 'selection' && (
                    <div className="barca-chat-selection bw-flex-1 bw-p-6 bw-flex bw-flex-col bw-items-center bw-justify-center">
                      <div className="bw-text-center bw-mb-8">
                        <h2
                          className="bw-text-2xl bw-font-bold bw-mb-3"
                          style={{
                            background:
                              'linear-gradient(to right, #A91B60, #004D98)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            color: 'transparent',
                          }}
                        >
                          Chat with Ryan
                        </h2>
                        <p className="bw-text-slate-700 bw-font-medium">
                          Choose how you'd like to communicate
                        </p>
                      </div>
      
                      <div className="bw-space-y-5 bw-w-full">
                        <button
                          onClick={() => selectChatMode('voice')}
                          className="barca-chat-voice-btn group bw-w-full bw-p-5 bw-rounded-2xl bw-backdrop-blur-sm bw-transition-all bw-duration-300 hover:bw-scale-105 hover:bw-shadow-xl"
                          style={{
                            background:
                              'linear-gradient(to bottom right, rgba(192, 24, 90, 0.1), rgba(0, 77, 184, 0.15))',
                          }}
                        >
                          <div className="bw-flex bw-items-center bw-gap-4">
                            <div className="bw-relative">
                              <div
                                className="bw-w-14 bw-h-14 bw-rounded-2xl bw-flex bw-items-center bw-justify-center group-hover:bw-scale-110 bw-transition-transform bw-duration-300 bw-shadow-lg"
                                style={{
                                  background:
                                    'linear-gradient(to bottom right, #C0185A, #004DB8)',
                                  boxShadow:
                                    '0 10px 15px -3px rgba(0, 77, 184, 0.2), 0 4px 6px -2px rgba(0, 77, 184, 0.05)',
                                }}
                              >
                                <Mic className="bw-w-7 bw-h-7 bw-text-white" />
                              </div>
                              <div
                                className="bw-absolute bw--inset-1 bw-rounded-2xl bw-blur bw-opacity-30 group-hover:bw-opacity-50 bw-transition-opacity bw-duration-300"
                                style={{
                                  background:
                                    'linear-gradient(to bottom right, #C0185A, #004DB8)',
                                }}
                              />
                            </div>
                            <div className="bw-text-left">
                              <div className="bw-font-bold bw-text-slate-800 bw-text-lg">
                                Voice Chat
                              </div>
                              <div className="bw-text-slate-600 bw-text-sm bw-font-medium">
                                Have a conversation with Ryan
                              </div>
                            </div>
                          </div>
                        </button>
      
                        <button
                          onClick={() => selectChatMode('text')}
                          className="barca-chat-text-btn group bw-w-full bw-p-5 bw-rounded-2xl bw-backdrop-blur-sm bw-transition-all bw-duration-300 hover:bw-scale-105 hover:bw-shadow-xl"
                          style={{
                            background:
                              'linear-gradient(to right, rgba(192, 24, 90, 0.1), rgba(0, 77, 184, 0.15))',
                          }}
                        >
                          <div className="bw-flex bw-items-center bw-gap-4">
                            <div className="bw-relative">
                              <div
                                className="bw-w-14 bw-h-14 bw-rounded-2xl bw-flex bw-items-center bw-justify-center group-hover:bw-scale-110 bw-transition-transform bw-duration-300 bw-shadow-lg"
                                style={{
                                  background:
                                    'linear-gradient(to bottom right, #004DB8, #C0185A)',
                                  boxShadow:
                                    '0 10px 15px -3px rgba(192, 24, 90, 0.2), 0 4px 6px -2px rgba(192, 24, 90, 0.05)',
                                }}
                              >
                                <MessageCircle className="bw-w-7 bw-h-7 bw-text-white" />
                              </div>
                              <div
                                className="bw-absolute bw--inset-1 bw-rounded-2xl bw-blur bw-opacity-30 group-hover:bw-opacity-50 bw-transition-opacity bw-duration-300"
                                style={{
                                  background:
                                    'linear-gradient(to bottom right, #004DB8, #C0185A)',
                                }}
                              />
                            </div>
                            <div className="bw-text-left">
                              <div className="bw-font-bold bw-text-slate-800 bw-text-lg">
                                {config.textChatLabel}
                              </div>
                              <div className="bw-text-slate-600 bw-text-sm bw-font-medium">
                                {config.textChatDescription}
                              </div>
                            </div>
                          </div>
                        </button>
                      </div>
                    </div>
                  )}
      
                  {/* Voice Chat Mode */}
                  {chatMode === 'voice' && (
                    <div className="barca-chat-voice-mode bw-flex bw-flex-col bw-h-full">
                      <div className="bw-flex-1 bw-p-4 bw-min-h-0">
                        {conversation.status === 'connected' ||
                        (hasStarted && messages.length > 0) ? (
                          <div className="bw-h-full">
                            <div className="barca-chat-messages bw-h-full bw-overflow-y-auto bw-overscroll-contain">
                              <div className="bw-space-y-3 bw-pr-2">
                                {messages.length === 0 ? (
                                  <div className="bw-bg-white/50 bw-backdrop-blur-sm bw-rounded-2xl bw-p-5 bw-text-center bw-border bw-border-red-200/50 bw-shadow-lg bw-shadow-red-100/20">
                                    <p className="bw-text-slate-700 bw-font-medium">
                                      Hi! I'm Ryan, your AI assistant. Start speaking
                                      when you're ready.
                                    </p>
                                  </div>
                                ) : (
                                  <>
                                    {messages.map((message) => (
                                      <div
                                        key={message.id}
                                        className={cn(
                                          'barca-chat-message bw-rounded-2xl bw-p-4 bw-backdrop-blur-sm bw-shadow-sm break-words',
                                          message.type === 'user'
                                            ? 'bw-text-white bw-ml-8 bw-border bw-border-blue-600/30 bw-shadow-lg'
                                            : 'bw-bg-white/60 bw-text-slate-800 bw-border bw-border-gray-200/40 bw-shadow-lg bw-shadow-gray-100/15 bw-mr-8'
                                        )}
                                        style={
                                          message.type === 'user'
                                            ? {
                                                background:
                                                  'linear-gradient(to right, rgba(0, 77, 184, 0.85), #004DB8)',
                                                boxShadow:
                                                  '0 10px 15px -3px rgba(0, 77, 184, 0.2), 0 4px 6px -2px rgba(0, 77, 184, 0.05)',
                                              }
                                            : undefined
                                        }
                                      >
                                        <div className="bw-text-sm bw-font-medium">
                                          {convertUrlsToLinks(message.content)}
                                        </div>
                                        <p className="bw-text-xs bw-opacity-70 bw-mt-2 bw-font-semibold">
                                          {message.type === 'user' ? 'You' : 'Ryan'}
                                        </p>
                                      </div>
                                    ))}
                                    {isListening && <ListeningPlaceholder />}
                                  </>
                                )}
                                <div ref={messagesEndRef} />
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="bw-h-96 bw-flex bw-items-center bw-justify-center">
                            <div className="bw-text-center">
                              <div className="bw-relative bw-mb-6">
                                <button
                                  onClick={startConversation}
                                  disabled={conversation.status === 'connecting'}
                                  className={cn(
                                    'barca-chat-start-btn bw-w-24 bw-h-24 bw-rounded-3xl bw-flex bw-items-center bw-justify-center bw-mx-auto bw-shadow-2xl bw-transition-all bw-duration-300 hover:bw-scale-105',
                                    conversation.status === 'connecting' &&
                                      'bw-opacity-75 cursor-not-allowed'
                                  )}
                                  style={{
                                    background:
                                      'linear-gradient(to bottom right, #C0185A, #004DB8)',
                                    boxShadow:
                                      '0 25px 50px -12px rgba(0, 77, 184, 0.3)',
                                  }}
                                >
                                  <Mic className="bw-w-12 bw-h-12 bw-text-white" />
                                </button>
                              </div>
                              <h3 className="bw-font-bold bw-text-slate-800 bw-text-2xl bw-mb-4">
                                Ready to Chat
                              </h3>
                              <p className="bw-text-slate-600 bw-mb-4 bw-font-medium">
                                Tap the microphone to start
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
      
                  {/* Text Chat Mode */}
                  {chatMode === 'text' && (
                    <div className="barca-chat-text-mode bw-flex-1 bw-p-4">
                      <div className="bw-h-full bw-flex bw-items-center bw-justify-center">
                        <div className="bw-text-center">
                          <div className="bw-relative bw-mb-6">
                            <div
                              className="bw-w-24 bw-h-24 bw-rounded-3xl bw-flex bw-items-center bw-justify-center bw-mx-auto bw-shadow-2xl bw-animate-pulse bw-border bw-border-red-600/20"
                              style={{
                                background:
                                  'linear-gradient(to bottom right, #C0185A, #004DB8)',
                                boxShadow:
                                  '0 25px 50px -12px rgba(192, 24, 90, 0.3)',
                              }}
                            >
                              <MessageCircle className="bw-w-12 bw-h-12 bw-text-white" />
                            </div>
                            <div
                              className="bw-absolute bw--inset-2 bw-rounded-3xl bw-blur bw-opacity-30 bw-animate-pulse"
                              style={{
                                background:
                                  'linear-gradient(to bottom right, #C0185A, #004DB8)',
                              }}
                            />
                          </div>
                          <h3 className="bw-font-bold bw-text-slate-800 bw-text-2xl bw-mb-4">
                            Text Interface
                          </h3>
                          <p className="bw-text-slate-600 bw-font-medium">
                            Connect to your existing messaging system
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
};
